import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { type Order } from '@sg/graphql-schema';

import { type OrderingCart } from '@order/features/ordering';

import {
  BagCartPollingDocument,
  type BagCartPollingQuery,
} from '../../GraphQL/BagCartPolling.generated';
import { prepareCart } from '../../utils';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching cart data for use in cart polling.
 */
export const useFetchBagCartPolling = () => {
  const client = useClient();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const fetchBagCartPolling = useMemo(
    () =>
      fromPromise<FetchBagCartPollingResponse, void>(async () => {
        const response = await client
          .query<BagCartPollingQuery>(BagCartPollingDocument, {}, { requestPolicy: 'network-only' })
          .toPromise();

        const cart = prepareCart((response.data?.cart ?? {}) as Partial<Order>);

        return { cart };
      }),
    [client],
  );

  return { fetchBagCartPolling };
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type FetchBagCartPollingResponse = {
  cart: OrderingCart;
};
